import type { ComponentType, ReactNode } from 'react'
import type { IndexRouteObject, NonIndexRouteObject, RouteObject } from 'react-router'
import { isEmpty } from '@jhqn/utils'
import { pathToRegexp } from 'path-to-regexp'
import { createContext } from 'react'

interface CommonRoute {
  wrappers?: ComponentType<any>[]
  meta?: {
    /** 标题 */
    title?: string
    /** 是否隐藏 */
    hide?: boolean
  }
}

export interface IIndexRoute extends IndexRouteObject, CommonRoute {}

export interface INonIndexRoute extends NonIndexRouteObject, CommonRoute {
  children?: IRoute[]
}

export type IRoute = IIndexRoute | INonIndexRoute

/**
 * 转换路由组件
 * @param ele
 */
export const transformElement = (ele: ComponentType<any>): ReactNode => {
  const Element = ele
  return <Element />
}

/**
 * 转换路由对象
 * @param route
 */
export const transformRoute = (route: IRoute): RouteObject => {
  const { Component, children, wrappers, index, ...rest } = route
  if (wrappers) {
    if (!Component) throw new Error('wrappers 只能用在有 component 的路由上')

    /**
     * 转换路由包裹组件
     * @param wrapperRoute
     * @param level
     */
    const transformWrapper = (wrapperRoute = {} as IRoute, level = 0): IRoute => {
      if (level === wrappers.length + 1) return wrapperRoute

      return transformWrapper(
        Object.assign(level === 0 ? rest : {}, {
          path: level === wrappers.length ? route.path : '',
          Component: level === 0 ? Component : wrappers[wrappers.length - level],
          children: level === 0 ? children : [wrapperRoute],
        } as IRoute),
        level + 1
      )
    }

    return transformRoute(transformWrapper())
  }

  if (!Component) throw new Error('Component 必须存在')

  if (children) {
    return {
      ...rest,
      Component,
      children: children.filter(child => !child.meta?.hide).map(transformRoute),
    }
  }

  return {
    ...rest,
    Component,
  }
}

/**
 * 转换路由数组
 * @param routes
 */
export const transformRoutes = (routes: IRoute[]): RouteObject[] => routes.map(transformRoute)

/**
 * 遍历过滤空路由
 * @param routes
 */
export const filterEmptyRoutes = (routes: IRoute[]): IRoute[] => {
  const filter = (route: IRoute): IRoute[] => {
    if (isEmpty(route)) return []
    if (route.children) {
      return [{ ...route, children: route.children.flatMap(filter) }]
    }
    return [route]
  }

  return routes.flatMap(filter)
}

/**
 * 平铺路由及metaInfo
 * @param routes
 */
export const flattenRoutes = (routes: IRoute[]) => {
  const routesMap = new Map<string, { meta: IRoute['meta'] }>()

  const flatten = (route: IRoute, prePath = '/') => {
    const { path, meta } = route
    const normalizedPath = `/${prePath}/${path}`
      .replace(/\/+/, '/')
      .replace(/\*+/, '*')
      .replace(/(\/\*)+/, '/:noMatch')
      .replace(/\/$/, '')
    routesMap.set(normalizedPath, {
      meta,
    })
    if (route.children) route.children.forEach(child => flatten(child, normalizedPath))
  }

  flatten(routes[0])

  return routesMap
}

/** 路由上下文 */
export const RouteContext = createContext<Map<string, { meta: IRoute['meta'] }> | undefined>(undefined)

/** 获取路由上下文钩子 */
export const useRouteData = () => {
  const route = useContext(RouteContext)
  if (!route) throw new Error('useRouteData 必须在 RouteContext 中使用')

  return { route }
}

/** 获取路由元数据 */
export const useMetaData = () => {
  const { pathname } = useLocation()
  const { route } = useRouteData()
  const routeSize = route.size
  const entry = route.entries()
  let something: IRoute['meta']

  for (let i = 0; i < routeSize; i += 1) {
    const [key, value] = entry.next().value!
    try {
      // jLog([key, value], pathToRegexp(key).test(pathname))
      if (pathToRegexp(key).regexp.test(pathname)) {
        something = value.meta
        break
      }
    } catch (e) {
      jError([key, value], e)
    }
  }

  return something
}
